import { LanguagesEnum } from '@/api/enums';
import { ModifiedQuestion, QuestionType } from '@/client/quizzes';
import {
  questionIdOptions,
  questionType,
} from '@/client/security-culture/types';
import { FormikDropdown, FormikInput, FormikTextarea } from '@/components/form';
import { FormikInputNumber } from '@/components/form/FormikInputNumber';
import { FlexContainer } from '@/ui/styled-ui';
import { TrashIcon } from '@/utils/helpers';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { Field, useFormikContext } from 'formik';
import { Panel, PanelHeaderTemplateOptions } from 'primereact/panel';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledPanel = styled(Panel)`
  position: relative;
  margin-top: var(--medium-padding);
  .p-panel-header {
    border-radius: var(--xsmall-border-radius);
    border: 1px solid var(--beige-main);
    background: var(--beige-main);
    height: 90px;
    padding: 0 var(--semi-big-padding);
    &.collapsed {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
  .p-panel-content {
    background: var(--beige-main);
    border: 0;
    border-top: 1px solid var(--gray-opacity);
    padding: var(--default-padding) var(--semi-big-padding);
    label {
      color: var(--black-main);
    }
  }
  .upload > div {
    background: var(--white-main);
  }
  .option-text {
    border-radius: var(--xsmall-border-radius);
    border: 1px solid var(--gray-dark);
    background: var(--white-main);
  }
  .scale input {
    width: 100%;
  }
`;

const StyledDeleteContainer = styled(FlexContainer)`
  cursor: pointer;
  &:hover {
    svg path,
    span {
      fill: var(--red-main);
      color: var(--red-main);
    }
  }
  > span {
    font-size: var(--xxsmall-font-size);
    margin-left: var(--xsmall-padding);
  }
`;

const StyledTitle = styled.div`
  max-width: calc(100% - 120px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--black-main);
  font-size: var(--small-font-size);
  font-weight: 500;
  flex: 1;
`;

export type SurveyQuestionEditProps = {
  questionIndex: number;
  question: ModifiedQuestion;
  selectedTab: LanguagesEnum;
  onRemove: () => void;
  hasValidOption: boolean;
  hasFeedback: boolean;
};

export const SurveyQuestionEdit: React.FC<SurveyQuestionEditProps> = ({
  questionIndex,
  question,
  selectedTab,
  onRemove,
  hasValidOption,
  hasFeedback,
}) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<any>();

  const panelHeaderTemplate = (options: PanelHeaderTemplateOptions) => {
    const className = `${options.className} justify-content-space-between ${
      !options.collapsed ? 'collapsed' : ''
    }`;

    return (
      <FlexContainer className={className} gap={40}>
        <StyledTitle>
          {
            values?.questions?.[questionIndex]?.translations?.[selectedTab]
              ?.text
          }
        </StyledTitle>

        <FlexContainer className="flex-shrink-0" width="auto">
          <StyledDeleteContainer
            id={`delete-question-button-${questionIndex}`}
            justify="flex-end"
            className="ml-auto mr-3"
            width="auto"
            onClick={onRemove}
          >
            <TrashIcon />
            <span>{t('content.delete.question')}</span>
          </StyledDeleteContainer>
          {options.togglerElement}
        </FlexContainer>
      </FlexContainer>
    );
  };

  return (
    <StyledPanel
      id={`question-edit-panel-${questionIndex}`}
      key={questionIndex}
      headerTemplate={(options) => panelHeaderTemplate(options)}
      toggleable
      collapseIcon="pi pi-chevron-up"
      expandIcon="pi pi-chevron-down"
    >
      <FlexContainer gap={24} align="flex-start">
        <div className="field w-full mb-4 flex-1">
          <Field
            id={`questions.${questionIndex}.label`}
            name={`questions.${questionIndex}.label`}
            label={t('sc.question.id')}
            className="w-full white"
            component={FormikDropdown}
            options={questionIdOptions(t, values.questions)}
            placeholder={t('generic.select')}
            filter
            required
            {...preventDropdownBrowserTranslate}
          />
        </div>
        <div className="field w-full mb-4 flex-1">
          <Field
            id={`questions.${questionIndex}.type`}
            name={`questions.${questionIndex}.type`}
            label={t('sc.question.type')}
            className="w-full white"
            component={FormikDropdown}
            options={questionType(t, hasFeedback)}
            placeholder={t('generic.select')}
            required
          />
        </div>
      </FlexContainer>
      <div className="field w-full flex-1">
        <Field
          id={`questions.${questionIndex}.translations.${selectedTab}.text`}
          name={`questions.${questionIndex}.translations.${selectedTab}.text`}
          label={t('sc.question.text')}
          placeholder={t('sc.question.text.placeholder')}
          rows={3}
          cols={30}
          className="w-full white"
          component={FormikTextarea}
          required={!hasValidOption}
        />
      </div>
      {question.type === QuestionType.RATING_SCALE && (
        <>
          <FlexContainer gap={16} align="flex-start" className="mb-4">
            <div className="field w-3 mb-0">
              <Field
                id={`questions.${questionIndex}.scaleMin`}
                name={`questions.${questionIndex}.scaleMin`}
                label={t('sc.question.scale.from')}
                className="w-full white scale"
                component={FormikInputNumber}
                showButtons={false}
                required={!hasValidOption}
                min={0}
                step={1}
                allowEmpty
              />
            </div>
            <div
              className="field w-full flex-1 mb-0"
              style={{ marginTop: '26px' }}
            >
              <Field
                id={`questions.${questionIndex}.translations.${selectedTab}.scaleMinLabel`}
                name={`questions.${questionIndex}.translations.${selectedTab}.scaleMinLabel`}
                placeholder={t('sc.question.scale.placeholder')}
                className="w-full white"
                component={FormikInput}
              />
            </div>
          </FlexContainer>
          <FlexContainer gap={16} align="flex-start">
            <div className="field w-3 mb-0">
              <Field
                id={`questions.${questionIndex}.scaleMax`}
                name={`questions.${questionIndex}.scaleMax`}
                label={t('sc.question.scale.to')}
                className="w-full white scale"
                component={FormikInputNumber}
                min={1}
                step={1}
                allowEmpty
                showButtons={false}
                required={!hasValidOption}
              />
            </div>
            <div
              className="field w-full flex-1 mb-0"
              style={{ marginTop: '26px' }}
            >
              <Field
                id={`questions.${questionIndex}.translations.${selectedTab}.scaleMaxLabel`}
                name={`questions.${questionIndex}.translations.${selectedTab}.scaleMaxLabel`}
                placeholder={t('sc.question.scale.placeholder')}
                className="w-full white"
                component={FormikInput}
              />
            </div>
          </FlexContainer>
        </>
      )}
    </StyledPanel>
  );
};
